import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { diffDocs, schemaExtensions, type Block, type DiffBlock, type SnapshotMeta, type Span } from "@quill/shared";
import { api } from "../api";
import { useOnline } from "../hooks";
import { ConfirmDialog, Dialog } from "./Dialog";

const numberFormat = new Intl.NumberFormat();
const time = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const day = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" });

export function when(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const prefix = same(d, today) ? "Today" : same(d, yesterday) ? "Yesterday" : day.format(d);
  return `${prefix}, ${time.format(d)}`;
}

function describe(v: SnapshotMeta, all: SnapshotMeta[]): string {
  if (v.kind === "before-restore") {
    const source = all.find((s) => s.id === v.sourceId);
    return source ? `Before restoring ${when(source.createdAt)}` : "Before a restore";
  }
  if (v.kind === "manual") return v.label ?? "Saved by you";
  return "Automatic";
}

/**
 * Version history for one document: save a version now, browse versions, preview one,
 * and restore it. Restoring saves the current text as a version first.
 */
export function History({ docId, onClose, onRestored }: { docId: string; onClose(): void; onRestored(): void }) {
  const online = useOnline();
  const [versions, setVersions] = useState<SnapshotMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<SnapshotMeta | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.listSnapshots(docId).then(setVersions, () => setError("Couldn't load the history. Are you online?"));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  async function saveNow() {
    setSaving(true);
    try {
      await api.saveSnapshot(docId);
      await load();
    } catch {
      setError("Couldn't save a version. Are you online?");
    } finally {
      setSaving(false);
    }
  }

  if (previewing && versions) {
    return (
      <Preview
        docId={docId}
        version={previewing}
        // The list is newest first, so the version before this one is the next in it.
        previous={versions[versions.findIndex((v) => v.id === previewing.id) + 1] ?? null}
        title={describe(previewing, versions)}
        onBack={() => setPreviewing(null)}
        onRestored={onRestored}
      />
    );
  }

  return (
    <Dialog title="History" onClose={onClose}>
      <p className="hint history-intro">
        A version is saved every 15 minutes while you write. Restoring one keeps your current text as a version too.
      </p>
      <button className="secondary" onClick={saveNow} disabled={!online || saving}>
        {saving ? "Saving…" : "Save version now"}
      </button>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {versions && versions.length === 0 && <p className="hint">No versions yet.</p>}
      {versions && versions.length > 0 && (
        <ul className="version-list">
          {versions.map((v) => (
            <li key={v.id}>
              <button onClick={() => setPreviewing(v)} disabled={!online}>
                <span className="version-when">{when(v.createdAt)}</span>
                <span className="version-meta">
                  {describe(v, versions)} · {numberFormat.format(v.wordCount)} {v.wordCount === 1 ? "word" : "words"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="sheet-actions">
        <button className="primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}

type PreviewMode = "changes" | "full";

function Preview(props: {
  docId: string;
  version: SnapshotMeta;
  previous: SnapshotMeta | null;
  title: string;
  onBack(): void;
  onRestored(): void;
}) {
  const { docId, version, previous } = props;
  const [content, setContent] = useState<unknown>(null);
  // null: not loaded yet; undefined: there's no earlier version.
  const [previousContent, setPreviousContent] = useState<unknown>(previous ? null : undefined);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<PreviewMode>(previous ? "changes" : "full");
  const [confirming, setConfirming] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    api.previewSnapshot(docId, version.id).then((p) => setContent(p.content), () => setFailed(true));
    if (previous) api.previewSnapshot(docId, previous.id).then((p) => setPreviousContent(p.content), () => setFailed(true));
  }, [docId, version.id, previous]);

  const diff = useMemo(
    () => (content && previousContent ? diffDocs(previousContent, content) : null),
    [content, previousContent],
  );
  const loaded = mode === "full" ? !!content : !!diff;

  async function restore() {
    setConfirming(false);
    setRestoring(true);
    try {
      await api.restoreSnapshot(docId, version.id);
      props.onRestored();
    } catch {
      setFailed(true);
      setRestoring(false);
    }
  }

  return (
    <Dialog title={`Version from ${when(version.createdAt)}`} onClose={props.onBack}>
      <p className="hint">{props.title}</p>
      {previous ? (
        <fieldset className="segmented preview-mode">
          <legend className="visually-hidden">Show</legend>
          {(["changes", "full"] as const).map((m) => (
            <label key={m}>
              <input type="radio" name="preview-mode" value={m} checked={mode === m} onChange={() => setMode(m)} />
              <span>{m === "changes" ? "Changes" : "Full document"}</span>
            </label>
          ))}
        </fieldset>
      ) : (
        <p className="hint">This is the earliest version, so there's nothing to compare it with.</p>
      )}
      {mode === "changes" && diff && previous && (
        <p className="hint diff-summary">
          Compared with {when(previous.createdAt)}: {changeSummary(diff.wordsAdded, diff.wordsRemoved)}
        </p>
      )}
      <div
        className="version-preview"
        role="region"
        tabIndex={0}
        aria-label={mode === "changes" ? "Changes in this version" : "Version text"}
      >
        {failed ? (
          <p className="error">Couldn't load this version.</p>
        ) : !loaded ? (
          <p className="hint">Loading…</p>
        ) : mode === "full" ? (
          <ReadOnly content={content} />
        ) : (
          <Changes blocks={diff!.blocks} />
        )}
      </div>
      <div className="sheet-actions spread">
        <button className="quiet" onClick={props.onBack}>
          ‹ All versions
        </button>
        <button className="primary" disabled={!content || restoring} onClick={() => setConfirming(true)}>
          {restoring ? "Restoring…" : "Restore this version"}
        </button>
      </div>
      {confirming && (
        <ConfirmDialog
          title="Restore this version?"
          message={<p>The document will go back to this version. Your current text is saved in History first, so you can switch back.</p>}
          confirm="Restore"
          onConfirm={restore}
          onClose={() => setConfirming(false)}
        />
      )}
    </Dialog>
  );
}

function changeSummary(added: number, removed: number): string {
  const words = (n: number) => `${numberFormat.format(n)} ${n === 1 ? "word" : "words"}`;
  if (!added && !removed) return "no words added or removed.";
  if (!removed) return `${words(added)} added.`;
  if (!added) return `${words(removed)} removed.`;
  return `${words(added)} added, ${words(removed)} removed.`;
}

/** Unchanged blocks shown around each change; longer unchanged stretches are collapsed. */
const CONTEXT = 1;

/** The changed parts of a version: additions highlighted, removals struck through. */
function Changes({ blocks }: { blocks: DiffBlock[] }) {
  const changed = blocks.map((b) => b.op !== "same");
  if (!changed.some(Boolean)) return <p className="hint diff-none">No changes from the previous version.</p>;
  const near = (i: number) => changed.slice(Math.max(0, i - CONTEXT), i + CONTEXT + 1).some(Boolean);

  const out: ReactNode[] = [];
  for (let i = 0; i < blocks.length; ) {
    if (near(i)) {
      out.push(<DiffBlockView key={i} item={blocks[i]!} />);
      i++;
      continue;
    }
    const from = i;
    while (i < blocks.length && !near(i)) i++;
    const n = i - from;
    out.push(
      <p key={`gap-${from}`} className="diff-gap">
        ⋯ {numberFormat.format(n)} unchanged {n === 1 ? "paragraph" : "paragraphs"}
      </p>,
    );
  }
  return <div className="prose preview-page diff">{out}</div>;
}

/** ins/del aren't announced by most screen readers, so they get hidden words too. */
const Hidden = ({ children }: { children: ReactNode }) => <span className="visually-hidden">{children}</span>;

function SpanView({ span }: { span: Span }) {
  if (span.op === "added")
    return (
      <ins>
        <Hidden>[added: </Hidden>
        {span.text}
        <Hidden>]</Hidden>
      </ins>
    );
  if (span.op === "removed")
    return (
      <del>
        <Hidden>[removed: </Hidden>
        {span.text}
        <Hidden>]</Hidden>
      </del>
    );
  return <Fragment>{span.text}</Fragment>;
}

function DiffBlockView({ item }: { item: DiffBlock }) {
  const text =
    item.op === "changed" ? (
      item.spans.map((s, i) => <SpanView key={i} span={s} />)
    ) : item.op === "same" ? (
      item.block.text
    ) : (
      item.op === "added" ? <ins>{item.block.text}</ins> : <del>{item.block.text}</del>
    );
  const label = LABELS[item.op];
  return (
    <BlockView
      block={item.block}
      className={`diff-${item.op}`}
      text={
        <>
          {label && <Hidden>{label} </Hidden>}
          {text}
        </>
      }
    />
  );
}

const LABELS: Record<DiffBlock["op"], string | undefined> = {
  same: undefined,
  added: "Added paragraph:",
  removed: "Removed paragraph:",
  changed: "Changed paragraph:",
};

function BlockView({ block, className, text }: { block: Block; className: string; text: ReactNode }) {
  const props = { className };
  switch (block.kind) {
    case "h1":
      return <h1 {...props}>{text}</h1>;
    case "h2":
      return <h2 {...props}>{text}</h2>;
    case "h3":
      return <h3 {...props}>{text}</h3>;
    case "quote":
      return (
        <blockquote {...props}>
          <p>{text}</p>
        </blockquote>
      );
    case "bullet":
      return (
        <p {...props}>
          <span className="diff-marker" aria-hidden="true">
            •
          </span>
          {text}
        </p>
      );
    case "ordered":
      return (
        <p {...props}>
          <span className="diff-marker" aria-hidden="true">
            {block.n}.
          </span>
          {text}
        </p>
      );
    case "break":
      return <hr {...props} aria-label={className === "diff-same" ? "Scene break" : `Scene break ${className.slice(5)}`} />;
    default:
      return <p {...props}>{text}</p>;
  }
}

function ReadOnly({ content }: { content: unknown }) {
  const editor = useEditor({
    extensions: schemaExtensions(),
    content: content as object,
    editable: false,
    immediatelyRender: true,
    editorProps: { attributes: { class: "prose preview-page" } },
  });
  return <EditorContent editor={editor} />;
}
